import { Controller } from "@hotwired/stimulus";

// Onboarding Persistence Controller
// Handles saving and restoring form data during onboarding navigation
export default class extends Controller {
  static targets = ["form"];
  static values = {
    step: String,
    autoSave: { type: Boolean, default: true }
  };

  connect() {
    console.log("OnboardingPersistence controller connected for step:", this.stepValue);
    
    // Restore saved data when the form loads
    this.restoreFormData();
    
    // Set up auto-save if enabled
    if (this.autoSaveValue) {
      this.setupAutoSave();
    }
  }

  disconnect() {
    // Save form data when leaving the page
    this.saveFormData();
  }

  // Save form data to sessionStorage
  saveFormData() {
    if (!this.hasFormTarget) return;

    const formData = new FormData(this.formTarget);
    const data = {};
    
    // Convert FormData to a plain object
    for (let [key, value] of formData.entries()) {
      // Handle multiple values (like checkboxes)
      if (data[key]) {
        if (Array.isArray(data[key])) {
          data[key].push(value);
        } else {
          data[key] = [data[key], value];
        }
      } else {
        data[key] = value;
      }
    }

    // Save to sessionStorage with step-specific key
    const storageKey = `onboarding_${this.stepValue}_data`;
    sessionStorage.setItem(storageKey, JSON.stringify(data));
    
    console.log(`Saved form data for step ${this.stepValue}:`, data);
  }

  // Restore form data from sessionStorage
  restoreFormData() {
    if (!this.hasFormTarget) return;

    const storageKey = `onboarding_${this.stepValue}_data`;
    const savedData = sessionStorage.getItem(storageKey);
    
    if (!savedData) return;

    try {
      const data = JSON.parse(savedData);
      console.log(`Restoring form data for step ${this.stepValue}:`, data);
      
      // Populate form fields with saved data
      Object.entries(data).forEach(([key, value]) => {
        const field = this.formTarget.querySelector(`[name="${key}"]`);
        if (!field) return;

        if (field.type === 'checkbox' || field.type === 'radio') {
          // Handle checkboxes and radio buttons
          if (Array.isArray(value)) {
            value.forEach(v => {
              const specificField = this.formTarget.querySelector(`[name="${key}"][value="${v}"]`);
              if (specificField) specificField.checked = true;
            });
          } else {
            const specificField = this.formTarget.querySelector(`[name="${key}"][value="${value}"]`);
            if (specificField) specificField.checked = true;
          }
        } else if (field.tagName === 'SELECT') {
          // Handle select dropdowns
          field.value = value;
        } else {
          // Handle text inputs, textareas, etc.
          field.value = value;
        }
      });
      
      // Trigger change events to update any dependent UI
      this.formTarget.querySelectorAll('input, select, textarea').forEach(field => {
        field.dispatchEvent(new Event('change', { bubbles: true }));
      });
      
    } catch (error) {
      console.error('Error restoring form data:', error);
    }
  }

  // Set up auto-save functionality
  setupAutoSave() {
    if (!this.hasFormTarget) return;

    // Save data on form field changes
    this.formTarget.addEventListener('change', () => {
      this.saveFormData();
    });

    // Save data on input (for text fields)
    this.formTarget.addEventListener('input', this.debounce(() => {
      this.saveFormData();
    }, 500));
  }

  // Clear saved data for current step
  clearSavedData() {
    const storageKey = `onboarding_${this.stepValue}_data`;
    sessionStorage.removeItem(storageKey);
    console.log(`Cleared saved data for step ${this.stepValue}`);
  }

  // Clear all onboarding data (useful when onboarding is completed)
  clearAllOnboardingData() {
    const keys = Object.keys(sessionStorage).filter(key => key.startsWith('onboarding_'));
    keys.forEach(key => sessionStorage.removeItem(key));
    console.log('Cleared all onboarding data');
  }

  // Debounce utility function
  debounce(func, wait) {
    let timeout;
    return function executedFunction(...args) {
      const later = () => {
        clearTimeout(timeout);
        func(...args);
      };
      clearTimeout(timeout);
      timeout = setTimeout(later, wait);
    };
  }

  // Action to manually save form data
  save() {
    this.saveFormData();
  }

  // Action to manually restore form data
  restore() {
    this.restoreFormData();
  }
}
